import Button from "components/buttons";
import DynamicFormInput from "components/form/form";
import Notes from "components/notes";
import PageTitle from "components/pageTitle";
import TwoColumnLayout from "layouts/TwoColumnLayout";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import ConfirmationModal from "components/ConfirmationModal";
import { useTranslation } from "react-i18next";

import {
    LEGAL_TRAINING_PROVISION_REQUEST_STATUS,
} from "utils/constantValues";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { createLegalTrainingProvision, updateLegalTrainingProvision } from "services/legalTrainingProvision";
import { getStakeholders } from "services/getEmployees";

const CreateLegalTrainingProvisionInternalRequest = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [stakeholders, setStakeholders] = useState<any>([]);
    const [selectedStakeholder, setSelectedStakeholder] = useState("");
    const [showConfirmation, setShowConfirmation] = useState(false);

    const navigate = useNavigate();
    const contextData = useAuth();
    const location = useLocation();
    const editData = location.state?.request;

    const [formState, setFormState] = useState({
        written_request_text: editData?.written_request_text || "",
        request_letter_ref_no: editData?.request_letter_ref_no || "",
        request_letter_files: null as File | null,
        directive_file: null as File | null,
        topic: editData?.topic || "",
    });

    const fileInputRefRequestLetter = useRef<HTMLInputElement>(null);
    const fileInputRefDraftInstruction = useRef<HTMLInputElement>(null);

    const handleChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<typeof formState, "request_letter_files" | "directive_file">
    ) => {
        setFormState((prev) => ({ ...prev, [fieldName]: file }));
    };

    const handleFormSubmit = async () => {
        setSubmitting(true);
        const data = new FormData();

        for (const key in formState) {
            const value = formState[key as keyof typeof formState];
            if (value !== null) {
                data.append(key, value instanceof File ? value : String(value));
            }
        }

        try {
            if (editData) {
                const response = await updateLegalTrainingProvision(editData.id, data);
                if (response.id) navigate(`/legal-training-provision/${response.id}`);
                showToast(t("LegalTrainingProvisionUpdatedSuccess") || "Legal training provision updated successfully!", "success");
            } else {
                data.append("status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.submitted_internal.value);
                data.append("forward_status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.forwarded_to_coordinator.value);
                data.append("requested_by", "internal");
                data.append("requested_by_id", contextData.user?.user?.id ?? "");
                data.append(
                    "stackholder_id",
                    selectedStakeholder ?? ""
                );

                const selected = stakeholders.find((s: any) => s.id === selectedStakeholder);
                if (selected) {
                    data.append("first_name", selected.first_name);
                    data.append("last_name", selected.last_name);
                    data.append("email_user_organization", selected.email);
                    data.append("organization_kebele", selected.organization?.address?.kebele || "");
                    data.append("organization_zone", selected.organization?.address?.zone || "");
                    data.append("organization_state", selected.organization?.address?.state || "");
                    data.append("organization_name", selected.organization?.name || "");
                    data.append("organization_type", selected.organization?.type || "");
                }

                const response = await createLegalTrainingProvision(data);
                if (response.id) navigate(`/legal-training-provision/${response.id}`);
                showToast(t("LegalTrainingProvisionSubmittedSuccess") || "Legal training provision request submitted successfully!", "success");
            }

            // Clear file inputs
            if (fileInputRefRequestLetter.current) fileInputRefRequestLetter.current.value = "";
            if (fileInputRefDraftInstruction.current) fileInputRefDraftInstruction.current.value = "";
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToSubmitLegalTrainingProvision") || "Failed to submit legal training provision request.", "error");
        } finally {
            setSubmitting(false);
            setShowConfirmation(false);
        }
    };

    useEffect(() => {
        const fetchStakeholders = async () => {
            console.log("Token details:", { token: contextData.token, accessToken: contextData?.user?.access_token, userType: contextData.user?.user_type });
            console.log("Fetching stakeholders with token:", contextData.token);
            try {
                const clients = await getStakeholders(contextData.token);
                console.log("API response:", clients);
                console.log("Clients structure:", { type: typeof clients, isArray: Array.isArray(clients), length: clients?.length, sample: clients?.[0] });
                console.log("Results array:", clients.results);
                setStakeholders(clients.results || []);
                console.log("Set stakeholders to:", clients.results || []);
            } catch (error) {
                console.error("Error fetching stakeholders:", error);
                showToast(t("FailedToFetchStakeholders") || "Failed to fetch stakeholders.", "error");
                setStakeholders([]);
            }
        };

        fetchStakeholders();
    }, [contextData.token, showToast, t]);

    return (
        <div>
            <PageTitle
                icon="⚖"
                text={t("RequestForLegalTrainingProvision") || "Request for Legal Training Provision"}
                subtitle={t("LegalTrainingProvisionFormSubtitle") || "Please fill out the form below to submit a legal training provision request."}
                iconColor="text-purple-600"
                textColor="text-[#432101]"
            />

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setShowConfirmation(true);
                }}
            >
                <TwoColumnLayout
                    left={
                        <div className="flex flex-col py-[22px] px-[24px] bg-white rounded-xl">
                            <label className="block mb-2 text-sm font-medium text-[#747171]">
                                {t("Stakeholder") || "Stakeholder"}
                            </label>
                            <select
                                value={selectedStakeholder}
                                onChange={(e) => setSelectedStakeholder(e.target.value)}
                                className="border rounded-md p-2 w-full mb-4 text-sm font-medium text-[#747171]"
                            >
                                <option value="">{t("SelectStakeholder") || "Select Stakeholder"}</option>
                                {stakeholders.map((option: any) => (
                                    <option key={option.id} value={option.id}>
                                        {option.organization?.name}
                                    </option>
                                ))}
                            </select>

                            <DynamicFormInput
                                inputs={[
                                    {
                                        value: formState.topic,
                                        type: "text",
                                        label: t("Topic") || "Topic",
                                        name: "topic",
                                        onChange: handleChange,
                                        placeholder: t("TopicPlaceholder") || "Eg. Training topic",
                                    },
                                    {
                                        value: formState.written_request_text,
                                        type: "textarea",
                                        label: t("TrainingSummary") || "Training Summary",
                                        name: "written_request_text",
                                        onChange: handleChange,
                                        placeholder: t("TrainingSummaryPlaceholder") || "Eg. Training summary",
                                    },
                                    {
                                        type: "file",
                                        label: t("SummaryFile") || "Summary File",
                                        name: "request_letter_files",
                                        onChange: (file) => handleFileChange(file, "request_letter_files"),
                                    },
                                ]}
                            />

                            <br />
                            <div className="flex space-x-4 ml-auto">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        if (fileInputRefRequestLetter.current) fileInputRefRequestLetter.current.value = "";
                                        if (fileInputRefDraftInstruction.current) fileInputRefDraftInstruction.current.value = "";
                                        navigate(-1);
                                    }}
                                >
                                    {t("Cancel") || "Cancel"}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    onClick={() => {}}
                                >
                                    {submitting ? (
                                        <div className="flex items-center justify-center">
                                            {LoadingIcon} {t("Submitting") || "Submitting..."}
                                        </div>
                                    ) : (
                                        t("SubmitRequest") || "Submit Request"
                                    )}
                                </Button>
                            </div>
                        </div>
                    }
                    right={
                        <div className="w-full max-w-md space-y-4">
                            <Notes
                                title={t("Note") || "Note"}
                                text={t("LegalTrainingProvisionNoteText") || "Provide all required details for the legal training provision request."}
                                subtext={t("LegalTrainingProvisionNoteSubtext") || "Ensure the selected stakeholder is correct before submitting."}
                            />
                            <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h2 className="font-semibold">{t("DocumentRequirements") || "Document Requirements"}</h2>
                                        <p className="text-sm text-gray-500">
                                            {t("LegalTrainingProvisionDocRequirementsDesc") || "Include supporting documents like legal research notes or legislative drafts."}
                                        </p>
                                    </div>
                                </div>
                                <div className="border-t pt-2 space-y-2">
                                    <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                                        <span className="text-sm font-medium">{t("ConductLegalResearch") || "Conducting Legal Research"}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 hover:bg-gray-100 rounded-md cursor-pointer">
                                        <span className="text-sm font-medium">{t("LegislativeDrafting") || "Legislative Drafting"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                />
            </form>

            <ConfirmationModal
                open={showConfirmation}
                onConfirm={handleFormSubmit}
                onCancel={() => setShowConfirmation(false)}
                title={t("ConfirmSubmission") || "Confirm Submission"}
                message={t("ConfirmSubmissionMessage") || "Are you sure you want to submit this legal training provision request? Once submitted, it cannot be edited."}
                confirmText={t("Submit") || "Submit"}
                cancelText={t("Cancel") || "Cancel"}
                loading={submitting}
            />
        </div>
    );
};

export default CreateLegalTrainingProvisionInternalRequest;